import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Settings, LogOut, Save, Eye, EyeOff, Linkedin, Mail, Check, AlertCircle, Inbox, X as XIcon, FileText, Calendar, Building2, Target, Clock, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Settings state
  const [settings, setSettings] = useState({
    linkedin_url: "",
    notification_email: "",
    secondary_email: ""
  });
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [settingsError, setSettingsError] = useState("");

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState("");

  // Submissions state
  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [submissionsError, setSubmissionsError] = useState("");
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // submission object pending deletion
  const [deleting, setDeleting] = useState(false);

  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Check for existing session
  useEffect(() => {
    const savedToken = sessionStorage.getItem("adminToken");
    if (savedToken) {
      setToken(savedToken);
      setIsLoggedIn(true);
      fetchSettings(savedToken);
      fetchSubmissions(savedToken);
    }
  }, []);

  const fetchSettings = async (authToken) => {
    try {
      const response = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setSettings(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const fetchSubmissions = async (authToken) => {
    setSubmissionsLoading(true);
    setSubmissionsError("");
    try {
      const response = await axios.get(`${API}/intake`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setSubmissions(response.data || []);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      } else {
        setSubmissionsError("Failed to load submissions");
      }
    } finally {
      setSubmissionsLoading(false);
    }
  };

  const handleDeleteSubmission = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    setSubmissionsError("");
    try {
      await axios.delete(`${API}/intake/${confirmDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions((prev) => prev.filter((s) => s.id !== confirmDelete.id));
      if (selectedSubmission?.id === confirmDelete.id) {
        setSelectedSubmission(null);
      }
      setConfirmDelete(null);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      } else {
        setSubmissionsError(error.response?.data?.detail || "Failed to delete submission");
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError("");

    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setToken(response.data.token);
        sessionStorage.setItem("adminToken", response.data.token);
        setIsLoggedIn(true);
        fetchSettings(response.data.token);
        fetchSubmissions(response.data.token);
      }
    } catch (error) {
      setLoginError(error.response?.data?.detail || "Invalid password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Ignore logout errors
    }
    setToken("");
    sessionStorage.removeItem("adminToken");
    setIsLoggedIn(false);
    setPassword("");
  };

  const handleSettingsSave = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSettingsError("");
    setSettingsSaved(false);

    try {
      const response = await axios.put(`${API}/admin/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch (error) {
      setSettingsError(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setPasswordError("");
    setPasswordChanged(false);

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError("New passwords do not match");
      setIsLoading(false);
      return;
    }

    if (passwordForm.new_password.length < 8) {
      setPasswordError("New password must be at least 8 characters");
      setIsLoading(false);
      return;
    }

    try {
      await axios.put(`${API}/admin/password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPasswordChanged(true);
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
      setTimeout(() => setPasswordChanged(false), 3000);
    } catch (error) {
      setPasswordError(error.response?.data?.detail || "Failed to change password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setForgotError("");

    try {
      await axios.post(`${API}/admin/forgot-password`, {
        origin: window.location.origin
      });
      setForgotSent(true);
    } catch (error) {
      setForgotError(error.response?.data?.detail || "Failed to send reset email. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-cream-50 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-white shadow-xl overflow-hidden">
            <div className="bg-teal-700 p-6 text-center">
              <h1 className="font-display text-lg tracking-[0.15em] text-cream-50">ADMIN SETTINGS</h1>
              <p className="font-body text-cream-50/60 text-sm mt-1">The Parris Consulting Group</p>
            </div>

            {showForgotPassword ? (
              <form onSubmit={handleForgotPassword} className="p-8" data-testid="forgot-password-form">
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-cream-100 rounded-full flex items-center justify-center">
                    <Mail className="text-teal-900" size={28} />
                  </div>
                </div>

                {forgotSent ? (
                  <>
                    <h2 className="text-center font-display text-sm tracking-[0.1em] text-teal-900 mb-3">
                      RESET LINK SENT
                    </h2>
                    <p className="text-center font-body text-teal-900/70 mb-6">
                      If an admin email is configured, a password reset link has been sent. Check your inbox (and spam folder). The link expires in 1 hour.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setForgotSent(false);
                        setForgotError("");
                      }}
                      className="w-full bg-teal-700 text-cream-50 py-4 font-display text-xs tracking-[0.15em] hover:bg-teal-700 transition-colors"
                      data-testid="forgot-back-btn"
                    >
                      BACK TO SIGN IN
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-center font-body text-teal-900/70 mb-6">
                      We'll send a password reset link to the admin notification email on file.
                    </p>

                    {forgotError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-4 flex items-center gap-2">
                        <AlertCircle size={18} />
                        <span className="font-body text-sm">{forgotError}</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-gold-500 text-brand-black py-4 font-display text-xs tracking-[0.15em] hover:bg-gold-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="forgot-send-btn"
                    >
                      {isLoading ? "SENDING..." : "SEND RESET LINK"}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setForgotError("");
                      }}
                      className="block text-center mt-6 mx-auto font-body text-sm text-teal-900/60 hover:text-teal-900"
                      data-testid="forgot-cancel-btn"
                    >
                      ← Back to sign in
                    </button>
                  </>
                )}
              </form>
            ) : (
              <form onSubmit={handleLogin} className="p-8">
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-cream-100 rounded-full flex items-center justify-center">
                    <Lock className="text-teal-900" size={28} />
                  </div>
                </div>

                <p className="text-center font-body text-teal-900/70 mb-6">
                  Enter your admin password to access settings
                </p>

                {loginError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-4 flex items-center gap-2">
                    <AlertCircle size={18} />
                    <span className="font-body text-sm">{loginError}</span>
                  </div>
                )}

                <div className="relative mb-6">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="w-full bg-transparent border-b border-teal-900/20 focus:border-teal-900 py-3 pr-10 outline-none font-body"
                    data-testid="admin-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-teal-900/50 hover:text-teal-900"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !password}
                  className="w-full bg-teal-700 text-cream-50 py-4 font-display text-xs tracking-[0.15em] hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="admin-login-btn"
                >
                  {isLoading ? "SIGNING IN..." : "SIGN IN"}
                </button>

                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="block text-center mt-4 mx-auto font-body text-sm text-teal-900/60 hover:text-teal-900 underline-offset-4 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>

                <a
                  href="/"
                  className="block text-center mt-4 font-body text-sm text-teal-900/60 hover:text-teal-900"
                >
                  ← Back to website
                </a>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-cream-50">
      {/* Header */}
      <header className="bg-teal-700 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-display text-sm tracking-[0.15em] text-cream-50">ADMIN SETTINGS</h1>
            <p className="font-body text-cream-50/60 text-xs">The Parris Consulting Group</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-cream-50/70 hover:text-cream-50 transition-colors font-body text-sm"
            data-testid="admin-logout-btn"
          >
            <LogOut size={18} /> Sign Out
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Settings Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white shadow-lg"
          >
            <div className="border-b border-cream-100 p-4 flex items-center gap-3">
              <Settings className="text-gold-500" size={20} />
              <h2 className="font-display text-sm tracking-[0.1em] text-teal-900">SITE SETTINGS</h2>
            </div>

            <form onSubmit={handleSettingsSave} className="p-6 space-y-6">
              {settingsSaved && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 flex items-center gap-2"
                >
                  <Check size={18} />
                  <span className="font-body text-sm">Settings saved successfully!</span>
                </motion.div>
              )}

              {settingsError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 flex items-center gap-2">
                  <AlertCircle size={18} />
                  <span className="font-body text-sm">{settingsError}</span>
                </div>
              )}

              <div>
                <label className="flex items-center gap-2 font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase mb-2">
                  <Linkedin size={16} /> LinkedIn URL
                </label>
                <input
                  type="url"
                  value={settings.linkedin_url}
                  onChange={(e) => setSettings({ ...settings, linkedin_url: e.target.value })}
                  placeholder="https://linkedin.com/in/your-profile"
                  className="w-full bg-transparent border-b border-teal-900/20 focus:border-teal-900 py-3 outline-none font-body"
                  data-testid="settings-linkedin-input"
                />
                <p className="font-body text-xs text-teal-900/50 mt-1">Displayed in the website footer</p>
              </div>

              <div>
                <label className="flex items-center gap-2 font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase mb-2">
                  <Mail size={16} /> Notification Email
                </label>
                <input
                  type="email"
                  value={settings.notification_email}
                  onChange={(e) => setSettings({ ...settings, notification_email: e.target.value })}
                  placeholder="tracy@theparrisconsultinggroup.com"
                  className="w-full bg-transparent border-b border-teal-900/20 focus:border-teal-900 py-3 outline-none font-body"
                  data-testid="settings-email-input"
                />
                <p className="font-body text-xs text-teal-900/50 mt-1">Primary email for intake form notifications</p>
              </div>

              <div>
                <label className="flex items-center gap-2 font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase mb-2">
                  <Mail size={16} /> Alternate Notification Email
                </label>
                <input
                  type="email"
                  value={settings.secondary_email || ""}
                  onChange={(e) => setSettings({ ...settings, secondary_email: e.target.value })}
                  placeholder="your-alternate@example.com (optional)"
                  className="w-full bg-transparent border-b border-teal-900/20 focus:border-teal-900 py-3 outline-none font-body"
                  data-testid="settings-secondary-email-input"
                />
                <p className="font-body text-xs text-teal-900/50 mt-1">Optional — also receives a copy of every intake form notification</p>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gold-500 text-brand-black py-3 font-display text-xs tracking-[0.15em] hover:bg-gold-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="settings-save-btn"
              >
                <Save size={16} /> SAVE SETTINGS
              </button>
            </form>
          </motion.div>

          {/* Password Change Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white shadow-lg"
          >
            <div className="border-b border-cream-100 p-4 flex items-center gap-3">
              <Lock className="text-gold-500" size={20} />
              <h2 className="font-display text-sm tracking-[0.1em] text-teal-900">CHANGE PASSWORD</h2>
            </div>

            <form onSubmit={handlePasswordChange} className="p-6 space-y-6">
              {passwordChanged && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 flex items-center gap-2"
                >
                  <Check size={18} />
                  <span className="font-body text-sm">Password changed successfully!</span>
                </motion.div>
              )}

              {passwordError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 flex items-center gap-2">
                  <AlertCircle size={18} />
                  <span className="font-body text-sm">{passwordError}</span>
                </div>
              )}

              <div className="relative">
                <label className="font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase mb-2 block">
                  Current Password
                </label>
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  placeholder="Enter current password"
                  className="w-full bg-transparent border-b border-teal-900/20 focus:border-teal-900 py-3 pr-10 outline-none font-body"
                  data-testid="password-current-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-0 bottom-3 text-teal-900/50 hover:text-teal-900"
                >
                  {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              <div className="relative">
                <label className="font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase mb-2 block">
                  New Password
                </label>
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  placeholder="Enter new password (min 8 characters)"
                  className="w-full bg-transparent border-b border-teal-900/20 focus:border-teal-900 py-3 pr-10 outline-none font-body"
                  data-testid="password-new-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-0 bottom-3 text-teal-900/50 hover:text-teal-900"
                >
                  {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              <div>
                <label className="font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase mb-2 block">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  placeholder="Confirm new password"
                  className="w-full bg-transparent border-b border-teal-900/20 focus:border-teal-900 py-3 outline-none font-body"
                  data-testid="password-confirm-input"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || !passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password}
                className="w-full bg-teal-700 text-cream-50 py-3 font-display text-xs tracking-[0.15em] hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="password-change-btn"
              >
                CHANGE PASSWORD
              </button>
            </form>
          </motion.div>
        </div>

        {/* Submissions Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white shadow-lg mt-6"
          data-testid="submissions-section"
        >
          <div className="border-b border-cream-100 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Inbox className="text-gold-500" size={20} />
              <h2 className="font-display text-sm tracking-[0.1em] text-teal-900">INTAKE SUBMISSIONS</h2>
              {!submissionsLoading && (
                <span className="font-body text-xs text-teal-900/60 bg-cream-100 px-2 py-1">
                  {submissions.length} total
                </span>
              )}
            </div>
            <button
              onClick={() => fetchSubmissions(token)}
              className="font-body text-xs text-teal-900/60 hover:text-teal-900 transition-colors"
              data-testid="submissions-refresh-btn"
            >
              Refresh
            </button>
          </div>

          <div className="p-6">
            {submissionsError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-4 flex items-center gap-2">
                <AlertCircle size={18} />
                <span className="font-body text-sm">{submissionsError}</span>
              </div>
            )}

            {submissionsLoading ? (
              <p className="text-center font-body text-sm text-teal-900/50 py-8">Loading submissions...</p>
            ) : submissions.length === 0 ? (
              <div className="text-center py-12">
                <Inbox className="mx-auto text-teal-900/20 mb-3" size={36} />
                <p className="font-body text-sm text-teal-900/60">No submissions yet. New intake forms will appear here.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="submissions-table">
                  <thead>
                    <tr className="border-b border-cream-100">
                      <th className="text-left py-3 px-2 font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase">Name</th>
                      <th className="text-left py-3 px-2 font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase hidden sm:table-cell">Email</th>
                      <th className="text-left py-3 px-2 font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase hidden md:table-cell">Date</th>
                      <th className="text-right py-3 px-2 font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map((sub) => (
                      <tr
                        key={sub.id}
                        className="border-b border-cream-100/60 hover:bg-cream-50/50 cursor-pointer transition-colors"
                        onClick={() => setSelectedSubmission(sub)}
                        data-testid={`submission-row-${sub.id}`}
                      >
                        <td className="py-3 px-2 font-body text-sm text-teal-900">
                          {sub.first_name} {sub.last_name}
                          <div className="font-body text-xs text-teal-900/50 sm:hidden">{sub.email}</div>
                        </td>
                        <td className="py-3 px-2 font-body text-sm text-teal-900/80 hidden sm:table-cell">{sub.email}</td>
                        <td className="py-3 px-2 font-body text-sm text-teal-900/70 hidden md:table-cell">
                          {new Date(sub.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </td>
                        <td className="py-3 px-2 text-right">
                          <div className="flex items-center justify-end gap-3">
                            <button
                              onClick={(e) => { e.stopPropagation(); setSelectedSubmission(sub); }}
                              className="font-display text-xs tracking-[0.1em] text-teal-700 hover:text-teal-900 uppercase"
                              data-testid={`submission-view-btn-${sub.id}`}
                            >
                              View
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setConfirmDelete(sub); }}
                              className="text-teal-900/40 hover:text-red-600 transition-colors"
                              title="Delete submission"
                              aria-label="Delete submission"
                              data-testid={`submission-delete-btn-${sub.id}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </motion.div>

        {/* Submission Detail Modal */}
        <AnimatePresence>
          {selectedSubmission && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedSubmission(null)}
              data-testid="submission-detail-modal"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-white shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="bg-teal-700 p-5 flex items-center justify-between sticky top-0 z-10">
                  <div>
                    <h3 className="font-display text-sm tracking-[0.15em] text-cream-50">SUBMISSION DETAILS</h3>
                    <p className="font-body text-xs text-cream-50/60 mt-1">
                      {new Date(selectedSubmission.created_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedSubmission(null)}
                    className="text-cream-50/70 hover:text-cream-50 transition-colors"
                    data-testid="submission-modal-close-btn"
                  >
                    <XIcon size={22} />
                  </button>
                </div>

                <div className="p-6 space-y-5">
                  <div>
                    <h4 className="font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase mb-1">Contact</h4>
                    <p className="font-body text-lg text-teal-900">{selectedSubmission.first_name} {selectedSubmission.last_name}</p>
                    <p className="font-body text-sm text-teal-900/80">
                      <a href={`mailto:${selectedSubmission.email}`} className="text-teal-700 hover:underline">{selectedSubmission.email}</a>
                    </p>
                    {selectedSubmission.organization && (
                      <p className="font-body text-sm text-teal-900/70 mt-1 flex items-center gap-1">
                        <Building2 size={14} /> {selectedSubmission.organization}
                      </p>
                    )}
                  </div>

                  {selectedSubmission.ai_summary && (
                    <div className="bg-cream-50 border-l-4 border-gold-500 p-4">
                      <h4 className="font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase mb-2 flex items-center gap-1">
                        <FileText size={14} /> AI Summary
                      </h4>
                      <p className="font-body text-sm text-teal-900 italic leading-relaxed whitespace-pre-wrap">{selectedSubmission.ai_summary}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase mb-1 flex items-center gap-1">
                        <Target size={13} /> Support Type
                      </h4>
                      <p className="font-body text-sm text-teal-900">{selectedSubmission.support_type}</p>
                    </div>
                    <div>
                      <h4 className="font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase mb-1">Engagement For</h4>
                      <p className="font-body text-sm text-teal-900">{selectedSubmission.engagement_for}</p>
                    </div>
                    <div>
                      <h4 className="font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase mb-1 flex items-center gap-1">
                        <Clock size={13} /> Timeline
                      </h4>
                      <p className="font-body text-sm text-teal-900">{selectedSubmission.timeline || '—'}</p>
                    </div>
                    <div>
                      <h4 className="font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase mb-1 flex items-center gap-1">
                        <Calendar size={13} /> Referral Source
                      </h4>
                      <p className="font-body text-sm text-teal-900">{selectedSubmission.referral_source || '—'}</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase mb-2">Situation Description</h4>
                    <p className="font-body text-sm text-teal-900 leading-relaxed whitespace-pre-wrap bg-cream-50 p-4">
                      {selectedSubmission.situation_description}
                    </p>
                  </div>

                  <div className="pt-2 flex flex-wrap gap-3">
                    <a
                      href={`mailto:${selectedSubmission.email}?subject=Re: Your inquiry — The Parris Consulting Group`}
                      className="flex-1 bg-gold-500 text-brand-black py-3 font-display text-xs tracking-[0.15em] hover:bg-gold-700 transition-colors text-center"
                      data-testid="submission-reply-btn"
                    >
                      REPLY VIA EMAIL
                    </a>
                    <button
                      onClick={() => setConfirmDelete(selectedSubmission)}
                      className="px-5 bg-red-50 text-red-700 py-3 font-display text-xs tracking-[0.15em] hover:bg-red-100 transition-colors flex items-center gap-2"
                      data-testid="submission-modal-delete-btn"
                    >
                      <Trash2 size={14} /> DELETE
                    </button>
                    <button
                      onClick={() => setSelectedSubmission(null)}
                      className="px-6 bg-cream-100 text-teal-900 py-3 font-display text-xs tracking-[0.15em] hover:bg-cream-50 transition-colors"
                    >
                      CLOSE
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Delete Confirmation Dialog */}
        <AnimatePresence>
          {confirmDelete && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
              onClick={() => !deleting && setConfirmDelete(null)}
              data-testid="delete-confirm-dialog"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                className="bg-white shadow-2xl max-w-md w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex items-start gap-4 mb-5">
                    <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center flex-shrink-0">
                      <Trash2 className="text-red-600" size={22} />
                    </div>
                    <div>
                      <h3 className="font-display text-base tracking-[0.05em] text-teal-900 mb-2">Delete this submission?</h3>
                      <p className="font-body text-sm text-teal-900/70 leading-relaxed">
                        You're about to permanently delete the submission from{" "}
                        <strong className="text-teal-900">
                          {confirmDelete.first_name} {confirmDelete.last_name}
                        </strong>
                        {" "}({confirmDelete.email}). This cannot be undone.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setConfirmDelete(null)}
                      disabled={deleting}
                      className="flex-1 bg-cream-100 text-teal-900 py-3 font-display text-xs tracking-[0.15em] hover:bg-cream-50 transition-colors disabled:opacity-50"
                      data-testid="delete-cancel-btn"
                    >
                      CANCEL
                    </button>
                    <button
                      onClick={handleDeleteSubmission}
                      disabled={deleting}
                      className="flex-1 bg-red-600 text-white py-3 font-display text-xs tracking-[0.15em] hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="delete-confirm-btn"
                    >
                      {deleting ? "DELETING..." : "DELETE"}
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Back to website link */}
        <div className="text-center mt-8">
          <a
            href="/"
            className="font-body text-teal-900/60 hover:text-teal-900 transition-colors"
          >
            ← Back to website
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
